import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, TextFieldProps, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";
import { Field, Form, Formik, FormikContextType, useFormikContext, FormikHelpers } from "formik";
import { CheckboxWithLabel, Select, TextField } from "formik-material-ui";
import * as Yup from "yup";
import { BatchFormData, VolumeUnit } from "../../model/batch";
import { RootState, useAppDispatch } from "../../reduxStore";
import { LoadingState } from "../../state/loadingState";
import { clearMessage } from "../../state/messages";
import { NotificationSnackbar } from "../common/NotificationSnackbar";
import { ProductCard } from "./ProductCard";
import { ProductSearch } from "./ProductSearch";
import DatePickerField from "../common/DatePickerField";
import { fetchProducts } from "../../state/findProducts";
import { fetchProduct, resetProduct } from "../../state/getProduct";

interface Props {
  handleSave: (values: BatchFormData) => void;
  error?: string;
  isSaving?: boolean;
}

const emptyBatch = (): BatchFormData => ({
  date: new Date(),
  product: null,
  quantity: "",
  vintage: "",
  volume: "",
  volumeUnit: "l",
  batchId: "",
  gtin: "",
  abv: "",
  additionalInformation: "",
  productImageOutdated: false,
  sampleRequested: false,
});

const validationSchema = Yup.object({
  product: Yup.object().nullable().required("Valitse tuote"),
  quantity: Yup.number().required("Lukumäärä puuttuu").min(0),
  vintage: Yup.number().notRequired().min(0),
  volume: Yup.number().required("Tilavuus puuttuu").min(0),
  batchId: Yup.string().required("Erätunnus puuttuu"),
  gtin: Yup.string().required("EAN-koodi puuttuu"),
  abv: Yup.number().required("Alkoholiprosentti puuttuu").min(0),
});

interface NumberFieldWithUnitProps {
  name: string;
  units: string[];
}
const NumberFieldWithUnit = ({ name, units, ...props }: NumberFieldWithUnitProps & TextFieldProps) => {
  const [volumeLabel, setVolumeLabel] = useState<string>("Tilavuus (l)");
  const { values, setFieldValue }: FormikContextType<BatchFormData> = useFormikContext<BatchFormData>();
  const unitFieldName = `${name}Unit`;

  useEffect(() => {
    setVolumeLabel(`Tilavuus (${values.volumeUnit})`);
  }, [values, setFieldValue]);

  return (
    <Grid container direction="row" spacing={1}>
      <Grid item xs>
        <Field component={TextField} name={name} label={volumeLabel} type="number" fullWidth {...props} />
      </Grid>
      <Grid item>
        <FormControl>
          <InputLabel htmlFor={unitFieldName}>Yks.</InputLabel>
          <Field
            name={unitFieldName}
            component={Select}
            inputProps={{
              id: unitFieldName,
            }}
          >
            {units.map((unit: string) => (
              <MenuItem key={`${unitFieldName}-${unit}`} value={unit}>
                {unit}
              </MenuItem>
            ))}
          </Field>
        </FormControl>
      </Grid>
    </Grid>
  );
};

const BatchSection = () => {
  return (
    <Grid item container direction="column">
      <Grid item>
        <Typography variant="h3">Saapumiserä</Typography>
      </Grid>
      <Grid item container direction="row" spacing={4}>
        <Grid item sm={12} md={6}>
          <DatePickerField name="date" label="Päivämäärä" disableFuture inputVariant="outlined" />
          <Field component={TextField} name="batchId" label="Erätunnus" type="string" fullWidth required />
          <Field component={TextField} name="quantity" label="Tuotteiden lukumäärä" type="number" fullWidth required />
          <Box py={2}>
            <Field
              component={TextField}
              name="additionalInformation"
              label="Lisätiedot"
              variant="outlined"
              multiline
              rows={4}
              fullWidth
            />
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

const ProductSection = () => {
  const dispatch = useAppDispatch();
  const { values, setFieldValue }: FormikContextType<BatchFormData> = useFormikContext<BatchFormData>();
  const products = useSelector((state: RootState) => state.products.find.products);
  const productsLoading = useSelector((state: RootState) => state.products.find.loading);
  const productsError = useSelector((state: RootState) => state.products.find.error);
  const product = useSelector((state: RootState) => state.products.get.product);
  const productLoading = useSelector((state: RootState) => state.products.get.loading);
  const productError = useSelector((state: RootState) => state.products.get.error);
  const requestSample = values.vintage !== "" && values.vintage !== product?.vintage;

  const handleProductSearch = useMemo(() => (query: string) => dispatch(fetchProducts(query)), [dispatch]);
  const handleProductSelect = useMemo(() => (productNumber: string) => dispatch(fetchProduct(productNumber)), [
    dispatch,
  ]);
  const handleProductReset = useMemo(() => () => dispatch(resetProduct()), [dispatch]);

  useEffect(() => {
    setFieldValue("sampleRequested", values.vintage !== "" && values.vintage !== product?.vintage);
  }, [setFieldValue, values, product]);

  useEffect(() => {
    if (product && productLoading === LoadingState.Succeeded) {
      if (product.abv) setFieldValue("abv", product.abv);
      if (product.vintage) setFieldValue("vintage", product.vintage);
      if (product.volume) setFieldValue("volume", product.volume);
      if (product.gtin) setFieldValue("gtin", product.gtin);
    }
  }, [product, productLoading, setFieldValue]);

  return (
    <Grid item container direction="column">
      <Grid item>
        <Typography variant="h3">Tuote</Typography>
      </Grid>
      <Grid item container direction="row">
        <Grid item container direction="column" sm={12} md={6}>
          <Grid item xs>
            <ProductSearch
              name="product"
              onSearch={handleProductSearch}
              products={products}
              loading={productsLoading === LoadingState.Pending}
              error={productsError || productError}
              onSelect={handleProductSelect}
              onReset={() => {
                setFieldValue("abv", "");
                setFieldValue("vintage", "");
                setFieldValue("volume", "");
                setFieldValue("volumeUnit", VolumeUnit.L);
                setFieldValue("gtin", "");
                handleProductReset();
              }}
            />
          </Grid>
          <Grid item>
            <Field component={TextField} name="gtin" label="EAN" type="number" fullWidth required />
            <Field component={TextField} name="vintage" label="Vuosikerta" type="number" fullWidth />
            <NumberFieldWithUnit name="volume" units={["l", "cl"]} required />
            <Field
              component={TextField}
              name="abv"
              label="Alkoholiprosentti"
              type="number"
              fullWidth
              required
              InputProps={{ endAdornment: "%" }}
            />
          </Grid>
          <Grid item>
            <Box py={2}>
              <Field
                component={CheckboxWithLabel}
                name="productImageOutdated"
                type="checkbox"
                disabled={!product}
                Label={{ label: "Tuotekuva vanhentunut" }}
              />
              <Field name="sampleRequested" type="hidden" value={requestSample} />
            </Box>
          </Grid>
        </Grid>
        <Grid item container direction="column" md={6}>
          <Box
            px={{
              sm: 0,
              md: 4,
            }}
          >
            <ProductCard product={product} />
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

const ActionButtonsSection = ({ isSaving }: any) => {
  const { handleSubmit, handleReset, isSubmitting, dirty } = useFormikContext<BatchFormData>();
  return (
    <Grid item container direction="row" justify="center" spacing={2}>
      <Grid item>
        <Button type="button" onClick={handleReset}>
          Tyhjennä lomake
        </Button>
      </Grid>
      <Grid item>
        <Button
          variant="contained"
          color="primary"
          type="button"
          onClick={(e: any) => handleSubmit(e)}
          disabled={isSaving || isSubmitting || !dirty}
        >
          Tallenna
        </Button>
      </Grid>
    </Grid>
  );
};

export const NewBatchForm = ({ handleSave, error: submitError, isSaving }: Props): JSX.Element => {
  const dispatch = useAppDispatch();
  const [error, setError] = useState(submitError);
  const product = useSelector((state: RootState) => state.products.get.product);
  const productError = useSelector((state: RootState) => state.products.get.error);

  const submitForm = async (values: BatchFormData, { setSubmitting, resetForm }: FormikHelpers<BatchFormData>) => {
    try {
      setSubmitting(true);
      handleSave(values);
      resetForm();
    } catch (error) {
      setError(error.message);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={emptyBatch()}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={validationSchema}
      onSubmit={submitForm}
    >
      {({ values, errors, handleChange, handleReset, handleSubmit, isSubmitting, dirty }) => {
        const requestSample = values.vintage !== "" && values.vintage !== product?.vintage;
        return (
          <Form autoCorrect="off" noValidate autoComplete="off">
            <NotificationSnackbar text={error} severity="error" onClosed={() => dispatch(clearMessage())} />
            <NotificationSnackbar text={productError} severity="error" onClosed={() => dispatch(clearMessage())} />
            <Grid container direction="column" spacing={2}>
              <ProductSection />
              <BatchSection />
              <Grid item>
                {requestSample && (
                  <Alert severity="warning">
                    <AlertTitle>Tuotteen tietoja muutettu</AlertTitle>
                    <Box>Saapuneesta tuote-erästä on lähettävä näyte.</Box>
                  </Alert>
                )}
              </Grid>
              <ActionButtonsSection isSaving={isSaving} />
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
